import type { Region } from '~~/core/entities/Region';

import { PropertyTypeEnum } from '~/core/entities/CRMEnums';

import { getCleanStaticListingPath } from '../links/cleanStaticListingPath';
import { getRegionMetadata } from './utils';

const regionName = 'Jardim Paulistano';

const metaTags = getRegionMetadata(regionName, 'São Paulo', { gender: 'male' });

const region: Region = {
	name: regionName,
	state: 'sp',
	city: 'São Paulo',
	country: 'Brasil',
	path: '/bairros/jardim-paulistano',
	image:
		'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/banner.webp',
	metaTags,
	info: {
		title: '',
		description:
			'São Paulo é uma cidade vibrante e diversificada, conhecida por sua infraestrutura moderna, opções culturais ricas e oportunidades de negócios incomparáveis. Morar em São Paulo significa estar próximo de tudo: excelentes restaurantes, shoppings de alto padrão, parques, centros culturais e muito mais. Bairros como Jardins, Moema e Itaim Bibi oferecem uma qualidade de vida excepcional com suas ruas arborizadas e serviços de alto nível.</br></br>Optar pela Pilar para encontrar seu imóvel ideal em São Paulo garante acesso a uma seleção exclusiva de propriedades de alto padrão. Nossa equipe de corretores qualificados está pronta para oferecer suporte personalizado, desde a busca do imóvel perfeito até a finalização da compra. Com a Pilar, você encontra segurança, confiança e a certeza de estar fazendo o melhor investimento. Descubra o seu próximo lar em São Paulo com a Pilar.'
	},
	hero: {
		title: regionName,
		description:
			'Seu oásis verde entre as avenidas mais movimentadas de São Paulo.',
		backgroundImage:
			'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/banner.webp'
	},
	map: {
		title: `${regionName}: onde aproveitar o fim de semana, explorar e comer bem`,
		content: `Em um quadrilátero entre a rua Joaquim Antunes, e as avenidas Europa, Faria Lima e Rebouças, está escondido o maior segredo de São Paulo: o bairro Jardim Paulistano. Apesar da ótima localização, a região tem clima de cidade pequena, com ruas arborizadas e silenciosas.<br/><br/>Majoritariamente de casas, o Jardim Paulistano também conta com comércios em ruas estratégicas, incluindo alguns dos melhores restaurantes da cidade, como as casas de Helena Rizzo. E vale lembrar que ele é pertinho de Pinheiros, um dos bairros mais boêmios da cidade.<br><br>Se você busca tranquilidade no meio do caos paulista de segunda a segunda, com ótimas áreas de lazer público e um ar de cidade do interior, o Jardim Paulistano é o bairro ideal para se mudar com a família. Aqui, te convencemos que ele é para você.`,
		image:
			'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/map.webp',
		links: [
			{
				label: `Ver imóveis no ${regionName}`,
				url: getCleanStaticListingPath({
					propertyTypes: [],
					regions: [regionName],
					states: ['SP'],
					cities: ['São Paulo']
				})
			}
		]
	},
	content: [
		{
			info: {
				title: `Onde comer no ${regionName}`,
				content:
					'Com algumas das casas mais prestigiadas do Brasil, o Jardim Paulistano abriga ótimas opções gastronômicas. Só na rua Joaquim Antunes, você encontra as casas de Helena Rizzo, Padoca do Maní, Maní e Casa Manioca, além do estrelado Evvai, do chef Luiz Filipe Souza, que recebeu sua segunda estrela Michelin em 2024.<br><br>Para quem adora um late lunch e quer estender até o fim do dia, a pedida é o clássico de comida portuguesa Adega Santiago. Sente no quintal para aproveitar o fim de tarde acompanhado de uma taça de vinho.<br><br>Opções para a família toda? É claro que o Jardim Paulistano tem! Que tal uma pizzada no domingo com os sabores incríveis da Primo Basílico? O delivery também é impecável.'
			},
			images: [
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-1.webp',
					credits: 'Kamil Kalbarczyk'
				},
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-2.webp',
					credits: 'Jason Leung'
				},
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-3.webp'
				}
			]
		},
		{
			info: {
				title: `Os clubes do ${regionName}`,
				content:
					'No Jardim Paulistano não faltam opções de lazer ao ar livre. Rodeado de árvores e com boas praças, com certeza você vai encontrar um programa para fazer aos fins de semana, mas se você procura um local para praticar esportes, curtir uma piscina e até conhecer gente nova, dois clubes estão localizados muito próximos ao bairro. <br><br>Um dos mais tradicionais de São Paulo, o Esporte Clube Pinheiros está localizado na Av. Faria Lima, ao lado do Shopping Iguatemi. É conhecido por sua vasta gama de esportes e forma alguns dos principais atletas do país. <br><br>Já o Clube Hebraica reúne 22 mil sócios e conta com escola maternal e infantil, além de uma série de atividades voltadas para empreendedores, de co-working à cursos profissionalizantes. Opções de esportes e lazer também não faltam.',
				class: 'lg:col-start-2'
			},
			images: [
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-4.webp',
					class: 'row-start-2'
				},
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-5.webp',
					credits: 'Scott Web',
					class: 'row-start-2'
				}
			]
		},
		{
			info: {
				title: `Parques no ${regionName}`,
				content:
					'Podemos dizer que o Jardim Paulistano é um parque por si só, graças às suas ruas arborizadas e clima tranquilo, com poucos carros, mas o bairro também conta com ótimas opções públicas de lazer. No coração do bairro, a Praça Antônio Duarte do Amaral lota aos finais de semana com moradores, seus filhos e pets. Com direito a carrinho de água de coco, a praça se enche de crianças se divertindo em um ótimo playground e pessoas curtindo os momentos ao ar livre.<br><br>Pertinho de lá, a Praça Desembargador Manoel Gomes de Oliveira é um ambiente tranquilo a poucos passos da Faria Lima. Com um pequeno playground e academia ao ar livre, ela é perfeita para quem quer um pouco mais de sossego.<br><br>Em busca de um parque de verdade? Pegue sua bicicleta, aproveite a ciclovia e pedale até o Parque do Povo.'
			},
			images: [
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-6.webp'
				},
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-7.webp'
				}
			]
		},
		{
			info: {
				title: `Imóvel icônico no ${regionName}`,
				content:
					'Construída em um amplo terreno no Jardim Paulistano, a casa projetada por Isay Weinfeld conta com design contemporâneo, com paredes de pedra, em quatro níveis diferentes. Enquanto a garagem fica no subsolo, o térreo abriga salas de estar e jantar, piscina com raia de 22 metros, academia e cozinha. No primeiro andar, encontramos as quatro suítes com closets e, no segundo andar, um escritório com terraço.',
				class: 'lg:col-start-2'
			},
			images: [
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-8.webp',
					credits: 'OC546'
				},
				{
					url: 'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulistano/content-9.webp',
					credits: 'OC546',
					class: 'row-start-2 col-start-2'
				}
			]
		}
	],
	nearRegions: [
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/itaim-bibi/banner.webp',
			name: 'Itaim Bibi',
			href: '/bairros/itaim-bibi'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-paulista/banner.webp',
			name: 'Jardim Paulista',
			href: '/bairros/jardim-paulista'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/vila-nova-conceicao/banner.webp',
			name: 'Vila Nova Conceição',
			href: '/bairros/vila-nova-conceicao'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-america/banner.webp',
			name: 'Jardim América',
			href: '/bairros/jardim-america'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/pinheiros/banner.webp',
			name: 'Pinheiros',
			href: '/bairros/pinheiros'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/alto-de-pinheiros/banner.webp',
			name: 'Alto de Pinheiros',
			href: '/bairros/alto-de-pinheiros'
		},
		{
			image:
				'https://d37vfq26jjkkpt.cloudfront.net/bairros/jardim-europa/banner.webp',
			name: 'Jardim Europa',
			href: '/bairros/jardim-europa'
		}
	],
	questions: [
		{
			question: `Onde fica o ${regionName}?`,
			answer:
				'O Jardim Paulistano está localizado na cidade de São Paulo, na zona Oeste, no distrito de Pinheiros, sob a subprefeitura de mesmo nome. O bairro é rodeado por outros bairros importantes como Pinheiros, Jardim Europa e Vila Madalena. As principais avenidas de acesso são a Avenida Faria Lima, Avenida Europa e Avenida Rebouças, além da Rua Joaquim Antunes, que corta o bairro e conecta as principais vias da região.'
		},
		{
			question: `Como chegar ao ${regionName}?`,
			answer:
				'Para quem chega de carro, o acesso ao Jardim Paulistano é facilitado pela Avenida Faria Lima, que conecta o bairro a outras regiões de São Paulo, como Pinheiros, Vila Madalena e o centro da cidade, tornando a locomoção bastante prática. Além disso, a Avenida Europa e a Avenida Rebouças também são vias de acesso importantes para quem trafega pela região. Para quem utiliza transporte público, o bairro é servido por diversas linhas de ônibus que circulam pelas principais avenidas. O Jardim Paulistano também possui boas ciclovias, facilitando o acesso para quem prefere pedalar até o Parque do Povo ou aos bairros vizinhos.'
		},
		{
			question: `O que fazer no ${regionName}?`,
			answer:
				'O Jardim Paulistano  oferece diversas opções de lazer e cultura, durante o dia, você pode aproveitar as praças do bairro, como a Praça Antônio Duarte do Amaral, que fica cheia de famílias e pets aos finais de semana, com um ótimo playground e espaço para lazer ao ar livre. Outra opção de lazer é a Praça Desembargador Manoel Gomes de Oliveira, que oferece um ambiente calmo com playground e academia ao ar livre, perfeita para quem busca sossego. O bairro também conta com ótimos clubes como o Esporte Clube Pinheiros, um dos mais tradicionais de São Paulo, que oferece uma grande variedade de esportes, e o Clube Hebraica, que além das opções de esportes, promove atividades culturais e sociais para seus associados. Para quem prefere um contato com a natureza, o bairro é cercado por ruas arborizadas e está perto do Parque do Povo, acessível via ciclovia para quem gosta de pedalar.'
		},
		{
			question: `Onde comer no ${regionName}?`,
			answer:
				'O Jardim Paulistano abriga uma excelente cena gastronômica, com algumas das casas mais prestigiadas de São Paulo. Na Rua Joaquim Antunes, você encontra o Maní e a Padoca do Maní, da chef Helena Rizzo, que são destaque na gastronomia paulistana. Para uma experiência Michelin, o Evvai, do chef Luiz Filipe Souza, que recentemente conquistou sua segunda estrela, é uma parada obrigatória. Quem deseja um ambiente mais descontraído pode se deliciar com uma pizza na Primo Basílico, ou aproveitar o fim de tarde no Adega Santiago, com um menu português e um clima aconchegante para um late lunch.'
		}
	],
	seo: {
		title: `Quem se interessou por ${regionName} também procurou por`,
		links: [
			{
				title: 'Tipo de imóvel',
				links: [
					{
						label: 'Apartamento',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.APARTMENT],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Casa em condomínio',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.CONDO_HOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Cobertura',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.PENTHOUSE],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Garden',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.GARDEN],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					},
					{
						label: 'Terreno',
						route: getCleanStaticListingPath({
							propertyTypes: [PropertyTypeEnum.LAND],
							states: ['sp'],
							cities: ['São Paulo'],
							regions: [regionName]
						})
					}
				]
			},
			{
				title: 'Localização',
				links: [
					{
						label: 'Jardim Paulista, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim Paulista']
						})
					},
					{
						label: 'Vila Nova Conceição, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Vila Nova Conceição']
						})
					},
					{
						label: 'Jardim América, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Jardim América']
						})
					}
				]
			},
			{
				title: ' ',
				links: [
					{
						label: 'Higienópolis, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Higienópolis']
						})
					},
					{
						label: 'Brooklin, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Brooklin']
						})
					},
					{
						label: 'Moema, São Paulo',
						route: getCleanStaticListingPath({
							states: ['sp'],
							cities: ['São Paulo'],
							regions: ['Moema']
						})
					}
				]
			}
		],
		discoveryContent: {
			heading: `Mais sobre o ${regionName}`,
			paragraphs: [
				'O Jardim Paulistano é um dos segredos mais bem guardados de São Paulo, situado entre a rua Joaquim Antunes e as avenidas Europa, Faria Lima e Rebouças. Com ruas arborizadas, esse bairro majoritariamente residencial oferece uma experiência tranquila no meio da agitação paulistana.',
				'O bairro abriga renomados restaurantes, com destaque para as casas de Helena Rizzo e o estrelado Evvai. Para lazer, o Jardim Paulistano conta com a Praça Antônio Duarte do Amaral, sempre cheia aos fins de semana, e a Praça Desembargador Manoel Gomes de Oliveira, perfeita para relaxar. Os clubes, como o Esporte Clube Pinheiros e o Clube Hebraica, oferecem diversas opções esportivas e sociais. Apesar de ser um bairro majoritariamente de casas, os imóveis incluem construções icônicas, como as quatro casas geminadas assinadas pelo renomado arquiteto brutalista Vilanova Artigas e uma casa contemporânea projetada por Isay Weinfeld, tornando o Jardim Paulistano não apenas um excelente lugar para morar, mas um local de referência na cidade para quem aprecia o que há de mais exclusivo e criativo na arquitetura paulistana.'
			]
		}
	}
};

export default region;
